import React, { ComponentType, FunctionComponent, useCallback } from 'react'
import { connect } from 'react-redux'

import { AnyAction, bindActionCreators, Dispatch } from 'redux'

import {
  CallTimeParams,
  useGetBenefitsEventProps,
} from '@lyrahealth-inc/shared-app-logic/src/features/benefits/hooks/useGetBenefitsEventProps'
import { WELLNESS_TOOLKIT_EVENTS_PROPERTIES } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/mixpanelConstants'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { SEARCHER_TYPE_TO_MATCH_FOR } from '@lyrahealth-inc/shared-app-logic/src/features/searchForCare/constants'
import { BenefitGroup } from '@lyrahealth-inc/shared-app-logic/src/models/benefit/Benefit'
import { toJS } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/hocs/toJS'

import { getAllBenefitsForUser, getRecommendedBenefitsForUser } from '../../data/benefits/benefitsSelector'
import { getCustomerId, getIsLyraNicotineQuitlineEnabled } from '../../data/customer/customerSelectors'
import { MixpanelEvent, trackEventWithObj } from '../../data/mixpanel/index'
import { getId, getUserCountryName } from '../../data/user/userSelectors'

type TrackIntegratedBenefitsParams = CallTimeParams & {
  [WELLNESS_TOOLKIT_EVENTS_PROPERTIES.TOOLKIT_TOPIC]?: string
}

export type TrackIntegratedBenefits = (params: TrackIntegratedBenefitsParams) => void

export interface InjectedProps {
  _userLyraId: string
  _customerLyraId: number
  _registeredCountry: string
  _matchFor: SEARCHER_TYPE_TO_MATCH_FOR
  _recommendedBenefitGroupsForUser: BenefitGroup[]
  _allBenefitGroupsForUser: BenefitGroup[]
  _lyraNicotineQuitlineEnabled: boolean
  _trackEventWithObj: (event: MixpanelEvent) => void
}

export const withTrackIntegratedBenefits = <P extends object>(
  Component: ComponentType<P & { trackIntegratedBenefits: TrackIntegratedBenefits }>,
): ComponentType<Omit<P, 'trackIntegratedBenefits'>> => {
  const WithTrackIntegratedBenefits: FunctionComponent<P & InjectedProps> = ({
    _userLyraId,
    _customerLyraId,
    _registeredCountry,
    _matchFor,
    _recommendedBenefitGroupsForUser,
    _allBenefitGroupsForUser,
    _lyraNicotineQuitlineEnabled,
    _trackEventWithObj,
    ...rest
  }) => {
    const { maxBenefitsToShowInExplorer, isNeurodiversityCOEEnabled, isCriticalIncidentSupportToolkitEnabled } =
      useFlags()
    const getBenefitsEventProps = useGetBenefitsEventProps({
      registeredCountry: _registeredCountry,
      matchFor: _matchFor,
      userLyraId: _userLyraId,
      customerLyraId: _customerLyraId,
      recommendedBenefitGroupsForUser: _recommendedBenefitGroupsForUser,
      allBenefitGroupsForUser: _allBenefitGroupsForUser,
      lyraNicotineQuitlineEnabled: _lyraNicotineQuitlineEnabled,
      maxBenefitsToShowInExplorer,
      shouldShowNeurodiversityToolkit: isNeurodiversityCOEEnabled,
      shouldShowCriticalIncidentSupportToolkit: isCriticalIncidentSupportToolkitEnabled,
    })

    const trackIntegratedBenefits = useCallback(
      (params: TrackIntegratedBenefitsParams) => {
        const toolkitTopic = params[WELLNESS_TOOLKIT_EVENTS_PROPERTIES.TOOLKIT_TOPIC]
        _trackEventWithObj({
          event: params.event,
          page: params.entryPoint,
          ...getBenefitsEventProps(params),
          ...(toolkitTopic && { [WELLNESS_TOOLKIT_EVENTS_PROPERTIES.TOOLKIT_TOPIC]: toolkitTopic }),
          ...(params.additionalMixpanelEvent && { ...params.additionalMixpanelEvent }),
        })
      },
      [getBenefitsEventProps, _trackEventWithObj],
    )

    return <Component trackIntegratedBenefits={trackIntegratedBenefits} {...(rest as P)} />
  }

  const mapStateToProps = ($$state: any) => {
    return {
      _userLyraId: getId($$state),
      _customerLyraId: getCustomerId($$state),
      _matchFor: $$state.getIn(['triageDemographics', 'matchFor']),
      _recommendedBenefitGroupsForUser: getRecommendedBenefitsForUser($$state),
      _allBenefitGroupsForUser: getAllBenefitsForUser($$state),
      _registeredCountry: getUserCountryName($$state),
      _lyraNicotineQuitlineEnabled: getIsLyraNicotineQuitlineEnabled($$state),
    }
  }

  const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) => {
    return bindActionCreators(
      {
        _trackEventWithObj: trackEventWithObj,
      },
      dispatch,
    )
  }

  // @ts-ignore
  return connect(mapStateToProps, mapDispatchToProps)(toJS(WithTrackIntegratedBenefits))
}
